import { useState } from 'react';
import { Button, Chip, TextField } from '@nyce/admin-kit/ui';
import type { Cluster, ClusterInput } from '../../api';
import { api } from '../../session';
import {
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  TableScroller,
  useAsync,
  useNotice,
} from '../../components/common';
import {
  EditorDialog,
  EnvChip,
  EnvField,
  IdField,
  SLUG,
  SwitchField,
  byName,
  errorText,
  isDnsLabel,
  useEditorForm,
} from '../../components/registry';

export const PROJECT_ID = /^[a-z][a-z0-9-]{4,28}[a-z0-9]$/;
export const LOCATION = /^[a-z]+-[a-z]+\d+(-[a-z])?$/;

type Form = ClusterInput & { id: string };

const empty: Form = {
  id: '',
  name: '',
  projectId: '',
  location: 'europe-west1',
  clusterName: '',
  environment: 'production',
  enabled: true,
};

const toForm = ({ updatedAt: _u, ...c }: Cluster): Form => c;

function ClusterForm({
  initial,
  isNew,
  onSaved,
  onClose,
}: {
  initial: Form;
  isNew: boolean;
  onSaved: (c: Cluster) => void;
  onClose: () => void;
}) {
  const f = useEditorForm<Form>(initial, isNew);
  const { v, set } = f;
  const projectErr =
    v.projectId && !PROJECT_ID.test(v.projectId) ? 'GCP project id, e.g. nyce-prod-123' : undefined;
  const locationErr =
    v.location && !LOCATION.test(v.location) ? 'region or zone, e.g. europe-west1(-b)' : undefined;
  const nameErr =
    v.clusterName && !isDnsLabel(v.clusterName) ? 'lowercase letters, digits and dashes' : undefined;
  const canSave =
    SLUG.test(v.id) &&
    !!v.name.trim() &&
    PROJECT_ID.test(v.projectId) &&
    LOCATION.test(v.location) &&
    isDnsLabel(v.clusterName);
  const save = () =>
    void f.run(async () => {
      const { id, ...body } = v;
      onSaved(await api.admin.clusters.put(id, { ...body, name: body.name.trim() }));
    });
  return (
    <EditorDialog
      title={isNew ? 'New cluster' : `Cluster ${v.id}`}
      error={f.error}
      busy={f.busy}
      canSave={canSave}
      onSave={save}
      onClose={onClose}
    >
      <TextField label="Name" value={v.name} onChange={(e) => set('name', e.target.value)} required />
      <IdField value={v.id} isNew={isNew} onChange={f.setId} example="logic-prod" />
      <TextField
        label="GCP project"
        value={v.projectId}
        onChange={(e) => set('projectId', e.target.value.trim())}
        error={projectErr}
        required
      />
      <TextField
        label="Location"
        value={v.location}
        onChange={(e) => set('location', e.target.value.trim())}
        error={locationErr}
        hint="region (regional cluster) or zone"
        required
      />
      <TextField
        label="GKE cluster name"
        value={v.clusterName}
        onChange={(e) => set('clusterName', e.target.value.trim())}
        error={nameErr}
        required
      />
      <EnvField value={v.environment} onChange={(e) => set('environment', e)} />
      <SwitchField label="Enabled" checked={v.enabled} onChange={(e) => set('enabled', e)} />
    </EditorDialog>
  );
}

export function ClustersPage() {
  const list = useAsync(() => api.admin.clusters.list(), []);
  const [editing, setEditing] = useState<{ value: Form; isNew: boolean } | null>(null);
  const notice = useNotice();
  const rows = [...(list.data ?? [])].sort(byName);

  const remove = async (c: Cluster) => {
    try {
      await api.admin.clusters.remove(c.id);
      notice.success(`Deleted ${c.name || c.id}`);
      await list.reload();
    } catch (e) {
      notice.error(`Could not delete ${c.name || c.id}: ${errorText(e)}`);
    }
  };

  return (
    <Page
      title="Clusters"
      description="GKE clusters the hub talks to. Clusters in the GCP projects the hub can see are added automatically; disable one to hide it."
      actions={<Button onClick={() => setEditing({ value: { ...empty }, isNew: true })}>Add</Button>}
    >
      <ErrorBox error={list.error} />
      <Loading loading={list.loading && !list.data} />
      {list.data && !rows.length && (
        <EmptyState
          card
          title="No clusters yet"
          hint="Add the GKE clusters that host nyce.logic environments."
        />
      )}
      {!!rows.length && (
        <div className="nyk-card">
          <TableScroller>
            <table className="nyk-ui-table nyk-cards" data-testid="clusters-table">
              <thead>
                <tr>
                  <th>Cluster</th>
                  <th>Environment</th>
                  <th>Project</th>
                  <th>Location</th>
                  <th>GKE name</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id} data-row={c.id}>
                    <td className="nyk-card-title">
                      <b>{c.name || c.id}</b>
                      <div className="nyk-ui-hint">{c.id}</div>
                      {c.source === 'discovered' && (
                        <Chip size="sm" className="nyk-tone-grey">
                          discovered
                        </Chip>
                      )}
                      {!c.enabled && (
                        <Chip size="sm" className="oph-off">
                          disabled
                        </Chip>
                      )}
                    </td>
                    <td data-label="Environment">
                      <EnvChip env={c.environment} />
                    </td>
                    <td data-label="Project" className="nyk-url">
                      {c.projectId}
                    </td>
                    <td data-label="Location">{c.location}</td>
                    <td data-label="GKE name" className="nyk-url">
                      {c.clusterName}
                    </td>
                    <td className="nyk-row-actions">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditing({ value: toForm(c), isNew: false })}
                      >
                        Edit
                      </Button>
                      <ConfirmButton
                        size="sm"
                        variant="danger"
                        message={`Delete cluster ${c.name || c.id}?`}
                        onConfirm={() => void remove(c)}
                      >
                        Delete
                      </ConfirmButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroller>
        </div>
      )}
      {editing && (
        <ClusterForm
          initial={editing.value}
          isNew={editing.isNew}
          onClose={() => setEditing(null)}
          onSaved={(c) => {
            setEditing(null);
            notice.success(`Saved ${c.name || c.id}`);
            void list.reload();
          }}
        />
      )}
      {notice.element}
    </Page>
  );
}
